import React, { useCallback, useRef, useState } from 'react'
import { Alert, Button, message, Modal } from '@surecompanies/core_components'
import InboxOutlined from '@ant-design/icons/InboxOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import type { BulkImportSessionResponseDto } from '../api/dto/bulk-import-session.dto'
import { formatFileSize } from '../utils/formatFileSize'

interface Props {
  open: boolean
  onClose: () => void
  onImport: (file: File) => Promise<BulkImportSessionResponseDto>
  onDownloadTemplate?: () => void
}

// Intentionally reduced from 15 MB — backend rejects payloads above 5 MB.
const MAX_FILE_SIZE = 5 * 1024 * 1024

type Phase = 'idle' | 'uploading'

export default function BulkImportCsvModal({
  open,
  onClose,
  onImport,
  onDownloadTemplate,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const closedMidUploadRef = useRef(false)

  const resetState = useCallback(() => {
    setPhase('idle')
    setSelectedFile(null)
    setErrorMessage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleClose = useCallback(() => {
    if (phase === 'uploading') {
      closedMidUploadRef.current = true
    }
    resetState()
    onClose()
  }, [phase, resetState, onClose])

  const validateAndSetFile = useCallback((file: File) => {
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      message.error('Only CSV files are allowed.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error('File exceeds the 5 MB limit.')
      return
    }
    setSelectedFile(file)
    setErrorMessage(null)
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      validateAndSetFile(file)
      e.target.value = ''
    },
    [validateAndSetFile]
  )

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleImport = useCallback(async () => {
    if (!selectedFile) return
    closedMidUploadRef.current = false
    setPhase('uploading')
    setErrorMessage(null)
    try {
      await onImport(selectedFile)
      if (closedMidUploadRef.current) return
      handleClose()
    } catch (err: unknown) {
      if (closedMidUploadRef.current) return
      setPhase('idle')
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string } }
      }
      const status = axiosErr.response?.status
      if (status === 401 || status === 403) {
        message.error('You do not have permission to perform this action.')
      } else if (status === 422) {
        const serverMsg =
          axiosErr.response?.data?.message ?? 'CSV could not be processed.'
        setErrorMessage(serverMsg)
      } else {
        // eslint-disable-next-line no-console
        console.error('bulk import csv failed', err)
        message.error('Import failed. Please try again.')
      }
    }
  }, [selectedFile, onImport, handleClose])

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (phase !== 'uploading') setIsDragOver(true)
    },
    [phase]
  )

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)
      if (phase === 'uploading') return
      const file = e.dataTransfer.files?.[0]
      if (file) validateAndSetFile(file)
    },
    [phase, validateAndSetFile]
  )

  const isUploading = phase === 'uploading'

  return (
    <Modal
      title="Bulk Import CSV"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={560}
      destroyOnClose
      maskClosable={false}
    >
      <div className="csvModalBody">
        <p className="csvModalHint">
          Upload a CSV file with override rules. All rows will be imported with
          status <strong>INACTIVE</strong>.
          {onDownloadTemplate && (
            <>
              {' '}
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={onDownloadTemplate}
                style={{ padding: 0 }}
              >
                Download template
              </Button>
            </>
          )}
        </p>

        {errorMessage && <Alert type="error" showIcon message={errorMessage} />}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          disabled={isUploading}
          className="inputHidden"
          aria-label="Select CSV file"
        />

        {selectedFile ? (
          <div className="csvFilePreview">
            <FileTextOutlined className="csvFileIcon" />
            <span className="csvFilePreviewMeta">
              <span className="csvFileName">{selectedFile.name}</span>
              <span className="csvFileSize">
                {formatFileSize(selectedFile.size)}
              </span>
            </span>
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemoveFile}
              disabled={isUploading}
              aria-label="Remove file"
            />
          </div>
        ) : (
          <div
            className={`csvDropZone${isDragOver ? ' csvDropZoneActive' : ''}`}
            onClick={() => !isUploading && fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            aria-label="Upload CSV file"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ')
                fileInputRef.current?.click()
            }}
          >
            <InboxOutlined className="csvDropZoneIcon" />
            <span className="csvDropZoneText">
              Click or drag a CSV file here
            </span>
            <span className="csvDropZoneHint">Max 5 MB · .csv only</span>
          </div>
        )}

        <div className="csvModalFooter">
          <Button onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleImport}
            loading={isUploading}
            disabled={!selectedFile || isUploading}
          >
            Import
          </Button>
        </div>
      </div>
    </Modal>
  )
}
